import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import type { FC } from "react";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import ProviderSelect from "../products/ProviderSelect";
import { useProviders } from "../providers/useProviders";
import ComparisonPanes from "./components/ComparisonPanes";
import KpiSections from "./components/KpiSections";
import OrdersBarChart from "./components/OrdersBarChart";
import ProviderProductsTable from "./components/ProviderProductsTable";
import ReportFilterPanel from "./components/ReportFilterPanel";
import EurExchangeRateChip from "./components/EurExchangeRateChip";
import ReportHeader from "./components/ReportHeader";
import ReportSubsection from "./components/ReportSubsection";
import { providerSections } from "./kpiSections";
import { REPORTS } from "./reportCatalog";
import { formatActiveFilters } from "./statisticsFormat";
import { useAppliedFilters } from "./useAppliedFilters";
import { useProviderStatistics } from "./useProviderStatistics";

interface PaneFilters {
  readonly providerId: string | null;
  readonly from: string;
  readonly to: string;
}

function defaultPaneAFilters(): PaneFilters {
  return {
    providerId: null,
    from: dayjs().startOf("month").format(ISO_DATE_FORMAT),
    to: dayjs().format(ISO_DATE_FORMAT),
  };
}

function defaultPaneBFilters(): PaneFilters {
  return {
    providerId: null,
    from: dayjs().subtract(1, "month").startOf("month").format(ISO_DATE_FORMAT),
    to: dayjs().subtract(1, "month").endOf("month").format(ISO_DATE_FORMAT),
  };
}

interface PaneProps {
  readonly initialFilters: PaneFilters;
}

const ReportPane: FC<PaneProps> = ({ initialFilters }) => {
  const { draft, applied, isDirty, setDraft, apply } = useAppliedFilters<PaneFilters>(initialFilters);
  const { data, isLoading, error } = useProviderStatistics(applied.providerId, {
    from: applied.from,
    to: applied.to,
  });
  const { providers } = useProviders();
  const providerName = providers.find((provider) => provider.id === applied.providerId)?.name ?? "—";

  return (
    <Box>
      <ReportFilterPanel
        subject={
          <ProviderSelect
            size="small"
            providerId={draft.providerId ?? undefined}
            onChange={(providerId) => setDraft({ ...draft, providerId })}
          />
        }
        dateLabel="Zakres dat"
        range={draft}
        onRangeChange={({ from, to }) => setDraft({ ...draft, from, to })}
        onApply={apply}
        applyDisabled={!isDirty}
        activeFilters={formatActiveFilters(providerName, applied.from, applied.to)}
      />
      {!applied.providerId && (
        <Typography sx={{ color: "text.secondary" }}>Wybierz dostawcę, aby zobaczyć statystyki</Typography>
      )}
      {isLoading && <LinearProgress data-testid="loading-indicator" />}
      {error && <Alert severity="error">Nie udało się pobrać statystyk.</Alert>}
      {data && (
        <>
          <EurExchangeRateChip eurExchangeRate={data.eurExchangeRate} />
          <KpiSections sections={providerSections(data)} />
          <OrdersBarChart
            title="Ilość zamówień — podział na miesiące"
            buckets={data.ordersByMonth}
            granularity="MONTH"
          />
          <ReportSubsection
            title="Produkty dostawcy"
            description={`Ilość i wartość zamówień wg produktu — ${formatActiveFilters(data.providerName, applied.from, applied.to)}`}
          >
            <ProviderProductsTable rows={data.products} />
          </ReportSubsection>
        </>
      )}
    </Box>
  );
};

const ProviderReportPage: FC = () => (
  <Box>
    <ReportHeader report={REPORTS.providers} />
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Realny % anulacji uwzględnia warunki brzegowe ze specyfikacji.
      </Typography>
      <Tooltip
        arrow
        title="Warunek A: (data realizacji − data anulacji) < 100 dni. Warunek B: (data anulacji − data utworzenia) > 10 dni."
      >
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
      </Tooltip>
    </Box>
    <ComparisonPanes
      leftLabel="Porównanie A"
      rightLabel="Porównanie B"
      left={<ReportPane initialFilters={defaultPaneAFilters()} />}
      right={<ReportPane initialFilters={defaultPaneBFilters()} />}
    />
  </Box>
);

export default ProviderReportPage;
